/* eslint-disable no-unused-vars */
import { useWeb3Modal } from "@web3modal/wagmi/react";
import cn from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { useAccount } from "wagmi";
import Icon from "../../../../../components/Icon";
import Loader from "../../../../../components/Loader";
import SuccessPanel from "../../../../../components/Modal/SuccessPanel";
import TooltipGlobal from "../../../../../components/TooltipGlobal";
import useConfig from "../../../../../customHooks/useConfig";
import { useRWAClaimRewards } from "../../../../../customHooks/useRWAClaimRewards";
import { useToastNotification } from "../../../../../customHooks/useToastNotification";
import {
    _no,
    computeUSDAmount,
    formatDecimals,
    removeNonNumeric
} from "../../../../../utils";
import {
    CLAIM_INTEREST_TEXT,
    CLICK_TO_VIEW,
    COLLECTING_TEXT,
    COLLECT_FAILED_MESSAGE,
    COLLECT_SUCCESS,
    COMPOUND_INTEREST_TEXT,
    CONFIRM_TEXT,
    DEFAULT_DECIMALS,
    DONE_TEXT,
    TRY_AGAIN_TEXT,
    APPROVE_IN_WALLET_TEXT,
    APPROVING_TEXT,
    USDT_TOKEN_NAME,
    RWA_VAULT_DEFAULT_APPROVED_AMOUNT,
} from "../../../../../utils/config";
import Parameter from "../Parameter";
import Product from "../Product";
import styles from "./WithdrawQueue.module.sass";
import { useSelector } from "react-redux";
import { getContractByField } from "../../../../../ethers/contracts";
import { parseUnits, formatUnits } from "viem";
import { useFarmTokenAllowance } from "../../../../../customHooks/useFarmTokenAllowance";
import { Range } from "react-range";
import Checkbox from "../../../../../components/Checkbox"; // Import the existing Checkbox component

const DEFAULT_APPROVE_TEXT = "Authorize Spending for Vault";

const MIN_INPUT_AMOUNT = 0; // Define the minimum input amount

const DISCOUNT_VALUES = [-50, -25, -10, -5, -3, -1, 0];

const WithdrawModal = ({
    item,
    doRefetch,
    onDone,
    positionAmount,
    token
}) => {
    console.log("item", item);
    const { isConnected } = useAccount();
    const config = useConfig();
    const { linkBase } = config;
    const toastNotification = useToastNotification();
    const { open } = useWeb3Modal();
    

    const [buttonText, setButtonText] = useState(CLAIM_INTEREST_TEXT);
    const [selectedRewardOption, setSelectedRewardOption] = useState(COMPOUND_INTEREST_TEXT); // Default to Compound
    const [isLoading, setIsLoading] = useState(false);
    const [initiateSubmit, setInitiateSubmit] = useState(false);
    const [successfullClose, setSuccessfullClose] = useState();
    const [finishOpen, setFinishOpen] = useState(false); // Renamed from finishClose
    const [transactionHashLink, setTransactionHashLink] = useState("");
    const [topUpAmount, setTopUpAmount] = useState("");
    const [selectedToken, setSelectedToken] = useState(token);
    const [max, setMax] = useState(token.balanceFormatted);
    const [contractFunctionName, setContractFunctionName] = useState("depositAndClaimInterest");
    const [buttonApproveText, setButtonApproveText] = useState(DEFAULT_APPROVE_TEXT);

    const [selectedDiscount, setSelectedDiscount] = useState(0);
    const [discountedAmount, setDiscountedAmount] = useState(0); // New state for discounted amount

    const handleSliderChange = (values) => {
        setSelectedDiscount(values[0]);
    };

    const {
        chainId,
    } = config;

    const forwardingDepositContract = useMemo(() => {
            return getContractByField(chainId, item.contractField)
        },
        [chainId, item.contractField]
    );
        
    const { pricingFeeds } = useSelector(
        (state) => state.token
    );

    const { address } = useSelector(
        (state) => state.settings
    );

    const tokenPriceData = pricingFeeds.find(
        item => item.name === token.name
    );
    
    const convertedTopUpAmount = removeNonNumeric(
        topUpAmount || 0
    );

    const parsedConvertedTopUpAmount = parseUnits(
        convertedTopUpAmount.toString(),
        selectedToken.decimals
    );

    const pureAmountInUSD = computeUSDAmount(
        DEFAULT_DECIMALS,
        convertedTopUpAmount,
        tokenPriceData?.price
    );

    const rwaClaimRewards = useRWAClaimRewards({
        config,
        forwardingDepositContract,
        contractFunctionName,
        topUpAmount: parsedConvertedTopUpAmount,
    });

    const availableInterest = useMemo(() => {
        const interest = item.details.find(
            (detail) => detail.title === "Available Interest"
        );
        return interest?.content;
    }, [item.details]);

    const availableInterestConvertedAmount = removeNonNumeric(
        availableInterest || 0
    )

    const parsedAvailableInterestConvertedAmount = parseUnits(
        availableInterestConvertedAmount.toString(),
        selectedToken.decimals
    );

    const parsedRwaVaultDefaultApprovedAmount = parseUnits(
        RWA_VAULT_DEFAULT_APPROVED_AMOUNT.toString(),
        selectedToken.decimals
    );

    const amountForApprove = useMemo(() => {
        return parsedConvertedTopUpAmount + token.balance + parsedAvailableInterestConvertedAmount + parsedRwaVaultDefaultApprovedAmount;
    }, [parsedConvertedTopUpAmount, token.balance, parsedAvailableInterestConvertedAmount, parsedRwaVaultDefaultApprovedAmount]);

    const resetTokenAllowance = useFarmTokenAllowance({
        config,
        nftId: null,
        token: selectedToken,
        userAccount: address,
        amount: amountForApprove,
        farmContract: forwardingDepositContract,
        isReset: selectedToken.name === USDT_TOKEN_NAME,
    });

    const tokenAllowance  = useFarmTokenAllowance({
        config,
        nftId: null,
        token: selectedToken,
        userAccount: address,
        amount: amountForApprove,
        farmContract: forwardingDepositContract,
    });

    const COLLECT_SUCCESS_MESSAGE = `${COLLECT_SUCCESS}`;

    const handleError = (err) => {
        toastNotification.showTransactionErrorToast(
            err.message
        );
    };

    const handleQuickSelect = (option) => {
        const aboveZero = parseFloat(max) > 0;
        const amount = option === "max" && aboveZero
            ? formatDecimals(Math.min(max, item.capacity))
            : "";

        setTopUpAmount(
            amount
        );
    };

    const handleClaim = async () => {
        if (buttonText === DONE_TEXT) {
            onDone?.();
        };

        if (buttonText === TRY_AGAIN_TEXT) {
            setFinishOpen(false); // Updated from setFinishClose
            setTransactionHashLink("");
        };

        setInitiateSubmit(true);
        setIsLoading(true);
    };

    const handleApprove = () => {
        if (buttonApproveText !== DEFAULT_APPROVE_TEXT) {
            return;
        }

        const handleApproval = (allowance) => {
            allowance.approve({
                onConfirmation: () => {
                    setIsLoading(true);
                    setButtonApproveText(APPROVE_IN_WALLET_TEXT);
                },
                onApproving: () => {
                    setIsLoading(true);
                    setButtonApproveText(APPROVING_TEXT);
                },
                onApprovalComplete: (approval, spendingCap) => {
                    setIsLoading(false);
                    if (approval) {
                    const amount = formatUnits(spendingCap, token.decimals);
                    toastNotification.success(
                        `Approved ${amount} ${token.name} tokens - You can proceed now`
                    );
                    setButtonApproveText(DEFAULT_APPROVE_TEXT);
                    }
                },
                onError: (error) => {
                    setIsLoading(false);
                    toastNotification.showTransactionErrorToast(error.message);
                    setButtonApproveText(DEFAULT_APPROVE_TEXT);
                },
            });
        };

        if (selectedToken.name === USDT_TOKEN_NAME) {
            resetTokenAllowance.approve({
                onConfirmation: () => {
                    setIsLoading(true);
                    setButtonApproveText(APPROVE_IN_WALLET_TEXT);
                },
                onApproving: () => {
                    setIsLoading(true);
                    setButtonApproveText(APPROVING_TEXT);
                },
                onApprovalComplete: (approval) => {
                    setIsLoading(false);
                    if (approval) {
                        handleApproval(tokenAllowance);
                    }
                },
                onError: (error) => {
                    setIsLoading(false);
                    toastNotification.showTransactionErrorToast(error.message);
                    setButtonApproveText(DEFAULT_APPROVE_TEXT);
                },
            });
        } else {
            handleApproval(tokenAllowance);
        }
    };

    useEffect(() => {
        setSelectedToken(token);
        setMax(token.balanceFormatted);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token?.name, token?.balanceFormatted]);

    useEffect(() => {
        if (!initiateSubmit || rwaClaimRewards.isLoading) return;

        const handleTransaction = (action) => {
            action({
                amount: parsedConvertedTopUpAmount,
                onConfirmation: () => {
                    setIsLoading(true);
                    setButtonText(CONFIRM_TEXT);
                },
                onAccept: (transactionHash) => {
                    setIsLoading(true);
                    setButtonText(COLLECTING_TEXT);
                    setTransactionHashLink(`${linkBase}/tx/${transactionHash}`);
                },
                onComplete: (close) => {
                    setIsLoading(false);
                    if (close) {
                        setFinishOpen(true); // Updated from setFinishClose
                        const transactionLink = `${linkBase}/tx/${close.transactionHash}`;
                        const message = close.status
                            ? COLLECT_SUCCESS_MESSAGE
                            : COLLECT_FAILED_MESSAGE;
                        const toastContent = toastNotification.generateLinkContent(
                            transactionLink,
                            `${message} ${CLICK_TO_VIEW}`
                        );
                        toastNotification.showToast(close.status, toastContent);
                        setSuccessfullClose(close.status);
                        setButtonText(close.status ? DONE_TEXT : TRY_AGAIN_TEXT);
                    }
                    doRefetch && doRefetch();
                },
                onError: (error) => {
                    setIsLoading(false);
                    handleError(error);
                    setTransactionHashLink("");

                    // Revert button text to the selected option and re-append "+ Top-up" if necessary
                    const baseText =
                        selectedRewardOption === CLAIM_INTEREST_TEXT
                            ? CLAIM_INTEREST_TEXT
                            : COMPOUND_INTEREST_TEXT;

                    // Remove any existing "+ Top-up" before appending it again
                    const sanitizedText = baseText.replace(" + Top-up", "");

                    const updatedText = topUpAmount ? `${sanitizedText} + Top-up` : sanitizedText;

                    setButtonText(updatedText);
                },
            });
        };

        handleTransaction(rwaClaimRewards.performAction);

        setInitiateSubmit(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initiateSubmit]);

    useEffect(() => {
        if (!topUpAmount) {
            if (selectedRewardOption === CLAIM_INTEREST_TEXT) {
                setContractFunctionName("claimInterest");
            } else if (selectedRewardOption === COMPOUND_INTEREST_TEXT) {
                setContractFunctionName("compoundInterest");
            }
        } else {
            if (selectedRewardOption === CLAIM_INTEREST_TEXT) {
                setContractFunctionName("depositAndClaimInterest");
            } else if (selectedRewardOption === COMPOUND_INTEREST_TEXT) {
                setContractFunctionName("depositAndCompoundInterest");
            }
        }
    }, [selectedRewardOption, topUpAmount]);

    useEffect(() => {
        if (selectedRewardOption === CLAIM_INTEREST_TEXT) {
            setButtonText(CLAIM_INTEREST_TEXT);
        } else if (selectedRewardOption === COMPOUND_INTEREST_TEXT) {
            setButtonText(COMPOUND_INTEREST_TEXT);
        }
    }, [selectedRewardOption]);

    useEffect(() => {
        const calculateDiscountedAmount = () => {
            const discountFactor = 1 + selectedDiscount / 100;
            return (parseFloat(max) * discountFactor).toFixed(2);
        };

        const newDiscountedAmount = calculateDiscountedAmount();
        setDiscountedAmount(newDiscountedAmount); // Update the state
        setButtonText(`List for ${newDiscountedAmount} ${selectedToken.name}`); // Update button text
    }, [selectedDiscount, max, selectedToken.name]);
    
    const preApproved = _no(finishOpen)
        ? tokenAllowance.allowance && tokenAllowance.allowance >= amountForApprove
        : true;

    const showApproval = (_no(preApproved)
        && _no(finishOpen));

    const totalTokenAmount = topUpAmount
        ? parseFloat(convertedTopUpAmount) + parseFloat(rwaClaimRewards.estimatesGasFormatted || 0)
        : 0;

    const isTokenInsufficient = topUpAmount
        && totalTokenAmount > parseFloat(max)
        && _no(isLoading)
        && _no(finishOpen);

    const isInsufficient = topUpAmount
        && parseFloat(convertedTopUpAmount) > parseFloat(max)
        && _no(finishOpen);

    const isExceedingCapacity = topUpAmount
        && _no(isInsufficient)
        && convertedTopUpAmount > item.capacity
        && _no(finishOpen);

    const disableApprove = isInsufficient
            || isLoading
            || isTokenInsufficient
            || isExceedingCapacity;

    const disableClaim = isInsufficient
            || isLoading
            || isExceedingCapacity
            || isTokenInsufficient;

    const claimButtonText = useMemo(() => {
        // If no top-up amount, return the base button text without "+ Top-up"
        if (!topUpAmount) {
            return buttonText.replace(" + Top-up", ""); // Ensure "+ Top-up" is removed
        }

        if (isInsufficient) {
            return `Insufficient ${selectedToken.name} Balance`;
        } else if (isTokenInsufficient) {
            return `Insufficient Funds for Gas`;
        } else if (isExceedingCapacity) {
            return `Exceeds Vault Capacity`;
        } else if (parseFloat(convertedTopUpAmount) < parseFloat(MIN_INPUT_AMOUNT) && _no(finishOpen)) {
            return `Minimum Top-up is ${MIN_INPUT_AMOUNT} ${token.name}`;
        }

        // Remove any existing "+ Top-up" before appending it again
        const sanitizedText = buttonText.replace(" + Top-up", "");

        // Append "+ Top-up" only if the text is "Claim Interest" or "Compound Interest"
        if (sanitizedText === CLAIM_INTEREST_TEXT || sanitizedText === COMPOUND_INTEREST_TEXT) {
            return `${sanitizedText} + Top-up`;
        }

        return sanitizedText;
    }, [topUpAmount, isInsufficient, isTokenInsufficient, isExceedingCapacity, convertedTopUpAmount, finishOpen, buttonText, selectedToken.name, token.name]);

    const parameters = item.details.map((detail) => {
        if (detail.title === "Position Amount") {
            return {
                ...detail,
                content: `${(parseFloat(positionAmount) + parseFloat(topUpAmount || 0)).toFixed(2)} ${token.name}`,
            };
        }
        return detail;
    });

    return (
        <>
            <div className={styles.details}>
                <div className={cn("title-yellow", styles.title)}>
                    Withdraw Position
                </div>
                <div className={styles.row}>
                    {finishOpen && ( // Updated from finishClose
                        <div className={styles.successWrapper}>
                            <div className={styles.iconWrapper}>
                                <SuccessPanel />
                            </div>
                            <div style={{ visibility: "hidden" }}>12</div>
                            <div className={styles.text}>
                                {successfullClose
                                    ? COLLECT_SUCCESS_MESSAGE
                                    : COLLECT_FAILED_MESSAGE}
                            </div>
                        </div>
                    )}
                    {_no(finishOpen) && ( // Updated from finishClose
                        <div className={styles.col}>
                            <Product className={styles.product} item={item} />
                            <div className={styles.parameters}>
                                {parameters.map((x, index) => (
                                    <Parameter item={x} key={index} />
                                ))}
                                <div className={styles.extraParameters}>
                                    <div className={styles.rewardOptions}>
                                        <label className={styles.rewardOptionsLabel}>Discount Offered</label>
                                        <Range
                                            step={1}
                                            min={0}
                                            max={DISCOUNT_VALUES.length - 1}
                                            values={[DISCOUNT_VALUES.indexOf(selectedDiscount)]}
                                            onChange={(values) => handleSliderChange([DISCOUNT_VALUES[values[0]]])}
                                            renderTrack={({ props, children }) => (
                                                <div
                                                    {...props}
                                                    style={{
                                                        ...props.style,
                                                        height: "6px",
                                                        background: "#ccc",
                                                        borderRadius: "3px",
                                                        position: "relative",
                                                        marginTop: "20px", // Added margin at the top
                                                    }}
                                                >
                                                    {children}
                                                </div>
                                            )}
                                            renderThumb={({ props, isDragged }) => (
                                                <div
                                                    {...props}
                                                    style={{
                                                        ...props.style,
                                                        height: "20px",
                                                        width: "20px",
                                                        backgroundColor: "#999",
                                                        borderRadius: "50%",
                                                        position: "relative",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            position: "absolute",
                                                            top: "-30px", // Adjusted for better visibility
                                                            left: "50%",
                                                            transform: "translateX(-50%)",
                                                            fontSize: "12px",
                                                            fontWeight: "bold",
                                                            color: "#fff", // Changed font color to white
                                                            padding: "2px 5px", // Retained padding for spacing
                                                        }}
                                                    >
                                                        {selectedDiscount}%
                                                    </div>
                                                </div>
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className={styles.howItWorks}>
                                    <div className={styles.howItWorksTitle}>How it Works</div>
                                    <div className={styles.howItWorksContent}>
                                        The withdrawal queue is executed in order by time and with higher discounts being executed first. 
                                        Users can increase their discount offered at any time, or cancel their withdrawal request.
                                    </div>
                                </div>
                                <div className={styles.checkboxWrapper}>
                                    <Checkbox
                                        content="<span style='color:rgb(178, 184, 190);'>Cancel (returns vault tokens to wallet)</span>"
                                        className={styles.checkbox}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    <div className={styles.btnsWrapper}>
                        {isConnected && (
                            <>
                                {transactionHashLink && finishOpen && ( // Updated from finishClose
                                    <a
                                        className={cn(
                                            "button",
                                            "button-stroke",
                                            styles.button,
                                            styles.transaction
                                        )}
                                        href={transactionHashLink}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        View Transaction Details
                                    </a>
                                )}
                                <div className={styles.btns}>
                                    {
                                        <button
                                            onClick={handleClaim}
                                            className={cn(
                                                "button",
                                                styles.button,
                                                styles.full,
                                                { 
                                                    disabled: disableClaim,
                                                    [styles.disabled]: disableClaim
                                                }
                                            )}
                                        >
                                            {buttonText === COLLECTING_TEXT && <Loader className={styles.loader} />}
                                            <span>{claimButtonText}</span>
                                        </button>
                                    }
                                </div>
                            </>
                        )}
                        {_no(isConnected) && (
                            <div className={styles.btns}>
                                <button
                                    className={cn(
                                        "button",
                                        styles.button,
                                        styles.full
                                    )}
                                    onClick={() => open()}
                                >
                                    <span>Connect Wallet</span>
                                    <Icon name="info" size="20" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <TooltipGlobal />
        </>
    );
};

export default WithdrawModal;
